// Header.jsx
import React, { useState, useEffect, useRef } from 'react';
import '../css/header.css';
import SideMenu from './sidemenu.js';
import logo from '../img/LogoADAPT.png';
import userIcon from '../img/user.png';           // <— rinominato per non confliggere con user del contesto
import menu from '../img/hamburgerIcon.png';
import chatbotIcon from '../img/chatbotIcon.jpg';
import closeMenu from '../img/closeHamburgerIcon.png';
import logoutIcon from '../img/logoutIcon.jpg';
import { Link, useNavigate } from 'react-router-dom';
import ChatPopup from "./AIChatpopup";
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import PipelineDrawer from "./PipelineDrawer";
import { retrieveMineData } from '../Utility/ConsumeToken/consumeToken';

/* ===========================================
   TokenBadge: sincrono, fetch in useEffect
   =========================================== */
function TokenBadge({ topPx = 72 }) {
  const [tokens, setTokens] = useState(null);   // null = loading
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apitoken = await retrieveMineData();
        console.log('Retrieved token data:', apitoken);
        if (cancelled) return;

        const raw = apitoken?.tokens ?? apitoken?.credits ?? apitoken?.availableTokens;
        if (Number.isFinite(raw)) {
          setTokens(raw);              // success: mostriamo il badge
        } else {
          setHasError(true);           // risposta non valida → nascondi
        }
      } catch (e) {
        if (!cancelled) setHasError(true); // errore → nascondi
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Nascondi durante il loading o in caso di errore
  if (hasError || tokens === null) return null;

  return (
    <div
      className="token-badge"
      style={{ top: `${topPx}px` }}
      aria-live="polite"
      aria-label={`Your Tokens: ${tokens}`}
      title={`Your Tokens: ${tokens}`}
    >
      <span className="token-dot" />
      <span className="token-text">Your Tokens: <strong>{tokens}</strong></span>
    </div>
  );
}

function Header() {
  const [isChatOpen, setChatOpen] = useState(false);
  const openChat = () => setChatOpen(true);
  const closeChat = () => setChatOpen(false);
  const [isMenuActive, setIsMenuActive] = useState(false);
  const [menuIconSrc, setMenuIconSrc] = useState(menu);
  const [isUserMenuOpen, setUserMenuOpen] = useState(false);

  const { user, logout } = useAuth();            // <— prende user dal contesto
  const isAuthenticated = !!user;                // <— true se loggato

  const navigate = useNavigate();
  const location = useLocation();

  console.log('isAuthenticated:', isAuthenticated);

  const sideMenuRef = useRef(null);
  const menuIconRef = useRef(null);
  const userMenuRef = useRef(null);
  const headerRef = useRef(null);
  const [badgeTop, setBadgeTop] = useState(72);

  const toggleMenu = () => {
    setIsMenuActive((v) => !v);
    setMenuIconSrc((v) => (v === menu ? closeMenu : menu));
  };

  const toggleUserMenu = () => setUserMenuOpen((v) => !v);

  const handleLogout = async () => {
    sessionStorage.removeItem("chatMessages");
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMenuActive &&
        sideMenuRef.current &&
        !sideMenuRef.current.contains(event.target) &&
        menuIconRef.current &&
        !menuIconRef.current.contains(event.target)
      ) {
        setIsMenuActive(false);
        setMenuIconSrc(menu);
      }
      if (
        isUserMenuOpen &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target)
      ) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuActive, isUserMenuOpen]);

  // Misura l’altezza dell’header in modo sicuro
  useEffect(() => {
    const computeTop = () => {
      if (!headerRef.current) return;                 // guardia
      const rect = headerRef.current.getBoundingClientRect();
      setBadgeTop(Math.max(0, rect.height) + 8);
    };

    computeTop();

    if (!headerRef.current) return;
    const ro = new ResizeObserver(() => {
      if (!headerRef.current) return;                // guardia anche qui
      computeTop();
    });
    ro.observe(headerRef.current);

    const onResize = () => computeTop();
    window.addEventListener('resize', onResize);

    return () => {
      try { ro.disconnect(); } catch {}
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // ——— UI NON LOGGATO ———
  if (!isAuthenticated) {
    return (
      <>
        <header className="header" ref={headerRef}>
          <div className="logodiv">
            <Link to="/dashboard">
              <img src={logo} alt="Logo" className="logo-img" style={{marginLeft: '25px'}}/>
            </Link>
          </div>
          <nav className="nav">
            <ul className="nav-links">
              <li><Link className="textlink" to="/">Home</Link></li>
              <li><Link className="textlink" to="/documentation">Documentation</Link></li>
              <li><Link className="textlink" to="/pricing">Pricing</Link></li>
            </ul>
          </nav>
          <div className="icon">
            <Link to="/login">
              <img
                src={userIcon}
                alt="User"
                className="iconUser"
                style={{ cursor: "pointer" }}
              />
            </Link>
          </div>
        </header>
      </>
    );
  }

  // ——— UI LOGGATO ———
  return (
    <>
      <header className="header" ref={headerRef}>
        <div ref={menuIconRef} className="hamburgerdiv" onClick={toggleMenu}>
          <span>
            <img src={menuIconSrc} alt="IconMenu" className="iconMenu" />
          </span>
        </div>
        <div className="logodiv">
          <Link to="/dashboard">
            <img src={logo} alt="Logo" className="logo-img" />
          </Link>
        </div>
        <nav className="nav">
          <ul className="nav-links">
            <li><Link className="textlink" to="/dashboard">Dashboard</Link></li>
            <li><Link className="textlink" to="/createpipeline">Create Pipeline</Link></li>
            <li><Link className="textlink" to="/pricing">Pricing</Link></li>
          </ul>
        </nav>
        <div className="icon">
          <button className="buttonLink" onClick={openChat}>
            <img src={chatbotIcon} alt="ChatBot" className="iconChat" />
          </button>
          <div className="user-dropdown" ref={userMenuRef}>
            <img
              src={userIcon}
              alt="User"
              className="iconUser"
              onClick={toggleUserMenu}
              style={{ cursor: "pointer" }}
            />
            {isUserMenuOpen && (
              <div className="dropdown-menu">
                <button className="dropdown-item" onClick={handleLogout}>
                  <img src={logoutIcon} alt="Logout" className="logout-icon" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Badge fisso (overlay), non spinge i contenuti) — solo se loggato */}
      <TokenBadge topPx={badgeTop} />

      <div ref={sideMenuRef}>
        <SideMenu className={isMenuActive ? 'active' : ''} />
      </div>

      <ChatPopup isOpen={isChatOpen} onClose={closeChat} />
      <PipelineDrawer />
    </>
  );
}

export default Header;
